<template>
  <div
    class="c-user-select"
    onclick="this.querySelector('input').focus();"
    v-if="ready"
  >
    <multiselect
      class="c-form-multiselect"
      :class="{ 'has-error': error }"
      :options="options"
      :multiple="true"
      label="name"
      track-by="id"
      :taggable="false"
      placeholder="Select one or more users"
      groupValues="options"
      groupLabel="label"
      v-model="selected"
      @search-change="asyncFind"
    >
      <template slot="singleLabel" slot-scope="props">
        <!-- <img class="c-user-select__image" :src="props.option.img" :alt="props.option.name"> -->
        <span class="c-user-select__item">
          <span class="c-user-select__name"
            >{{ props.option
            }}{{ props.option.name || props.option.email }}</span
          >
        </span>
      </template>

      <template slot="noResult" v-if="loading">
        <p>Searching for results...</p>
      </template>
      <template slot="noResult" v-else>
        <p>No results found</p>
      </template>
      <template slot="noOptions">
        <p>There are no options available</p>
      </template>

      <template slot="option" slot-scope="props">
        <!-- <img class="c-user-select__image" :src="props.option.img" :alt="props.option.name"> -->
        <span class="c-user-select__item" v-if="props.option.name">
          <span class="c-user-select__name">{{ props.option.name }}</span>
          <span class="c-user-select__meta" v-if="props.option.meta">{{
            props.option.meta.label
          }}</span>
        </span>
        <span class="c-user-select__label" v-else>
          <span class="c-user-select__item">{{
            props.option.$groupLabel
          }}</span>
        </span>
      </template>

      <template slot="tag" slot-scope="{ option, remove }">
        <span class="c-form-multiselect__tag">
          <span>{{ option.name }}</span>
          <span class="c-form-multiselect__remove" @click="remove(option)">
            <icon name="cross" width="10" height="10"></icon>
          </span>
        </span>
      </template>
    </multiselect>

    <div class="c-form-field__drop-icon">
      <icon name="drop-arrow" width="8" height="8"></icon>
    </div>
    <div v-if="error">
      <p class="c-form__validation-message">{{ error }}</p>
    </div>
  </div>
</template>

<script>
/**
 * Let people choose groups and/or users.  Based on multiselect component
 * Need to get users and groups
 */
import { mapState } from "vuex";
import debounce from "lodash/debounce";
import UserHasPermission from "@/mixins/UserHasPermission";

export default {
  name: "UserGroupSelect",
  mixins: [UserHasPermission],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    groupsPermission: {
      type: Array,
      default: () => [],
    },
    adPermission: {
      type: Array,
      default: () => [],
    },
    userPermission: {
      type: Array,
      default: () => [],
    },
    error: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState({
      users: (state) => state.users.collection,
    }),
    options() {
      const optionsToReturn = [];
      if (
        this.groupsPermission.length == 0 ||
        this.userGroupCheck(this.groupsPermission) ||
        this.userGroupCheck(this.adPermission)
      ) {
        optionsToReturn.push({
          label: "My Groups",
          options: this.filteredUserGroups.map((group) => {
            return {
              id: `grp_${group.id}`,
              name: group.name,
              meta: {
                label: `${group.user_ids ? group.user_ids.length : 0} users`,
                data: group.user_ids,
              },
            };
          }),
        });
      }

      if (
        this.userPermission.length == 0 ||
        this.userGroupCheck(this.userPermission)
      ) {
        optionsToReturn.push({
          label: "Users",
          options: this.users.map((user) => {
            // if the user has no name, use the email instead
            return { ...user, name: user.name ? user.name : user.email };
          }),
        });
      }
      return optionsToReturn;
    },
  },
  data() {
    return {
      ready: false,
      selected: this.value,
      loading: false,
      filteredUserGroups: null,
    };
  },
  created() {
    this.$store.dispatch("users/listForSelect");
    this.$store
      .dispatch("UserGroups/list")
      .then((resp) => {
        this.filteredUserGroups = [];

        // if the user doesn't have the ad permissions, filter them out
        const adCheck = this.userGroupCheck(this.adPermission);
        const groupCheck = this.userGroupCheck(this.groupsPermission);
        if (
          this.adPermission.length == 0 ||
          (this.adPermission.length > 0 && groupCheck)
        ) {
          const userGroups = resp.filter(
            (group) => group.user_id == this.$store.state.auth.currentUser.id
          );
          this.filteredUserGroups = [...this.filteredUserGroups, ...userGroups];
        }

        // likewise, if the user DOES have ad permissions, we want to filter
        // out personal groups
        if (
          this.groupsPermission.length == 0 ||
          (this.groupsPermission.length > 0 && adCheck)
        ) {
          const adGroups = resp.filter((group) => group.user_id == null);
          this.filteredUserGroups = [...this.filteredUserGroups, ...adGroups];
        }
        this.ready = true;
      })
      .catch((err) => {
        this.ready = true;
      });
  },
  methods: {
    userGroupCheck(groups) {
      let inGroup = false;
      groups.forEach((group) => {
        if (this.isInGroup(group)) {
          inGroup = true;
        }
      });
      // super admins are allowed
      if (!inGroup) {
        return this.isSuperUser;
      }

      return inGroup;
    },
    asyncFind: debounce(function(query) {
      this.isLoading = true;
      this.$store.dispatch("users/listForSelect", query).then((response) => {
        this.users = response;
        this.isLoading = false;
      });
    }, 500),
  },
  watch: {
    /**
     * Just post the whole objects and let the parent deal with the data
     */
    selected(newValue, oldValue) {
      this.$emit("onChange", newValue);
    },
  },
};
</script>

<style scoped lang="scss">
.c-user-select {
  position: relative;
  &__item {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  &__meta {
    color: $colour-nhs-mid-grey;
  }
}
</style>
<style scoped lang="scss">
.has-error {
  .multiselect__tags {
    border-color: $colour-nhs-emergency-red;
  }
}
</style>
